import React from "react";
import { Checkbox, FormControlLabel, IconButton, Tooltip } from "@mui/material";
import {
  CheckBoxOutlineBlankOutlined,
  CheckBoxOutlined,
  FileDownloadOutlined,
} from "@mui/icons-material";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import AppConfirmDialog from "components/Shared/AppConfirmDialog";
import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import DateRangeToggle from "components/Shared/DateRangeToggle";
import { useLazyListDriverQuery } from "store/services/driverApi";
import { exportExcelFile } from "utils/exportExcelFile";

const EXCEL_DATA_INITIAL_STATE = {
  dateRange: [],
  licenceId: null,
};

const ExportDriverExcel = ({ isBusy = false, activeTab }) => {
  const [showDialog, setShowDialog] = React.useState(false);
  const [showForm, setShowForm] = React.useState(false);
  const [licences, setLicences] = React.useState([]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: EXCEL_DATA_INITIAL_STATE,
  });

  const [fetchDrivers, { isFetching: loadingLicences }] = useLazyListDriverQuery();

  // Fetch licenses for autocomplete
  React.useEffect(() => {
    fetchDrivers({ Active: activeTab !== "Inactive" })
      .unwrap()
      .then((response) => {
        const allLicences = response.data
          .flatMap((driver) => driver.driverLicence || [])
          .filter((licence) => licence?.isActive && licence?.licenceIdd); // Only active licenses
        setLicences(allLicences);
      })
      .catch((error) => {
        toast.error("Error fetching licences: " + error.message);
      });
  }, [fetchDrivers, activeTab]);

  const handleDownload = async (data) => {
    const { dateRange, licenceId } = data;
    const payload = {
      Active: activeTab !== "Inactive",
      fromDate: dateRange[0] ? new Date(dateRange[0]).toISOString() : undefined,
      toDate: dateRange[1] ? new Date(dateRange[1]).toISOString() : undefined,
    };

    try {
      const response = await fetchDrivers(payload).unwrap();

      if (!response?.data || response.data.length === 0) {
        toast.info("No records found for the selected date range.");
        return;
      }

      const groupedDrivers = {};

      response.data.forEach((row) => {
        const driverKey = row.driverId || "N/A";

        // Filter by licenceId if showForm is true and licenceId is selected
        if (
          showForm &&
          licenceId &&
          !row.driverLicence?.some((lic) => lic.licenceIdd === licenceId.licenceIdd)
        ) {
          return; // Skip drivers that don't match the selected licenceId
        }

        if (!groupedDrivers[driverKey]) {
          groupedDrivers[driverKey] = {
            SN: 0,
            "Driver ID": driverKey,
            Name: row.name || "N/A",
            Email: row.email || "N/A",
            Phone: row.phone || "N/A",
            "Medical Status": row.medicalCList?.name || "N/A",
            "Created Date": row.createdAt
              ? new Date(row.createdAt).toISOString().split("T")[0]
              : "N/A",
            "Licence IDs": new Set(),
            "Vehicle IDs": new Set(),
          };
        }

        // Collect all active licence IDs
        if (row.driverLicence && Array.isArray(row.driverLicence)) {
          row.driverLicence
            .filter((licence) => licence?.isActive && licence?.licenceIdd)
            .forEach((licence) => {
              groupedDrivers[driverKey]["Licence IDs"].add(licence.licenceIdd);
            });
        }

        // Collect all vehicle IDs
        if (row.vehicles && Array.isArray(row.vehicles)) {
          row.vehicles
            .filter((vehicle) => vehicle?.vehicle_id)
            .forEach((vehicle) => {
              groupedDrivers[driverKey]["Vehicle IDs"].add(vehicle.vehicle_id);
            });
        }
      });

      const formattedData = Object.values(groupedDrivers).map((driver, index) => ({
        ...driver,
        SN: index + 1,
        "Licence IDs": Array.from(driver["Licence IDs"]).join(", ") || "N/A",
        "Vehicle IDs": Array.from(driver["Vehicle IDs"]).join(", ") || "N/A",
      }));

      if (formattedData.length === 0) {
        toast.info("No drivers found for the selected filters.");
        return;
      }

      exportExcelFile({ sheetData: formattedData, fileName: `DriverList_${activeTab}` });
    } catch (error) {
      toast.error("Error fetching driver data: " + error.message);
    }

    handleReset();
  };

  const handleReset = () => {
    setShowDialog(false);
    setShowForm(false);
    reset(EXCEL_DATA_INITIAL_STATE);
  };

  return (
    <>
      <Tooltip title="Download Excel" arrow>
        <IconButton color="success" onClick={() => setShowDialog(true)}>
          <FileDownloadOutlined />
        </IconButton>
      </Tooltip>

      <AppConfirmDialog
        open={showDialog}
        onClose={handleReset}
        onConfirm={handleSubmit(handleDownload)}
        title="Download Driver Excel"
        dialogIcon={() => <FileDownloadOutlined color="success" />}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={showForm}
              onChange={(e) => setShowForm(e.target.checked)}
              icon={<CheckBoxOutlineBlankOutlined />}
              checkedIcon={<CheckBoxOutlined />}
            />
          }
          label="Filter by Licence ID"
        />

        {showForm && (
          <AppForm loading={isBusy || loadingLicences}>
            <AppForm.Content>
              <Controller
                name="licenceId"
                control={control}
                rules={{ required: showForm ? "Please select Licence ID." : false }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    label="License ID"
                    required={showForm}
                    value={value || null}
                    onChange={(_, data) => onChange(data)}
                    getOptionLabel={(option) => option?.licenceIdd || ""}
                    isOptionEqualToValue={(option, value) =>
                      option?.licenceIdd === value?.licenceIdd
                    }
                    options={licences}
                    loading={loadingLicences}
                    loadingText="Loading licence IDs..."
                    helperText={errors?.licenceId?.message}
                    error={!!errors?.licenceId}
                  />
                )}
              />
            </AppForm.Content>
          </AppForm>
        )}

        <Controller
          name="dateRange"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <DateRangeToggle {...field} label="Select Date Range" error={error?.message} />
          )}
        />
      </AppConfirmDialog>
    </>
  );
};

export default ExportDriverExcel;